import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';

interface StatsData {
  username: string;
  total_games: number;
  wins: number;
  losses: number;
  ties: number;
  win_rate: number;
}

interface PlayerStatsProps {
  socket: Socket | null;
  username: string;
  onClose: () => void;
}

export const PlayerStats: React.FC<PlayerStatsProps> = ({ socket, username, onClose }) => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!socket || !username) return;

    // Solicitar estadísticas
    socket.emit('get-stats', username);

    // Escuchar respuesta
    socket.on('stats-data', (data: StatsData) => {
      setStats(data);
      setLoading(false);
    });

    socket.on('stats-error', (error: string) => {
      console.error('Error al cargar estadísticas:', error);
      setLoading(false);
    });

    return () => {
      socket.off('stats-data');
      socket.off('stats-error');
    };
  }, [socket, username]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-retro-dark border-4 border-neon-cyan rounded-lg shadow-neon-cyan max-w-md w-full">
        {/* Header */}
        <div className="bg-neon-cyan/20 border-b-2 border-neon-cyan p-4 flex justify-between items-center">
          <h2 className="font-retro text-2xl text-neon-cyan animate-glow">
            📈 ESTADÍSTICAS
          </h2>
          <button
            onClick={onClose}
            className="ui-button text-neon-pink hover:text-neon-yellow transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-cyan animate-pulse">
                CARGANDO STATS...
              </p>
            </div>
          ) : !stats ? (
            <div className="text-center py-8">
              <p className="font-retro text-neon-yellow">
                NO SE ENCONTRARON DATOS
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Username */}
              <div className="text-center mb-6">
                <h3 className="font-retro text-3xl text-neon-yellow">
                  {stats.username}
                </h3>
              </div>

              {/* Win Rate Circle */}
              <div className="flex justify-center mb-6">
                <div className="relative w-32 h-32">
                  <svg className="transform -rotate-90" viewBox="0 0 120 120">
                    {/* Background circle */}
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      stroke="rgba(0, 255, 255, 0.2)"
                      strokeWidth="8"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="60"
                      cy="60"
                      r="54"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(stats.win_rate / 100) * 339} 339`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#39ff14" />
                        <stop offset="100%" stopColor="#00ffff" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-retro text-3xl text-neon-green">
                      {Number(stats.win_rate).toFixed(0)}%
                    </span>
                    <span className="font-retro text-xs text-neon-cyan">WIN RATE</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Total Games */}
                <div className="bg-neon-cyan/20 border-2 border-neon-cyan rounded-lg p-4 text-center">
                  <p className="font-retro text-3xl text-neon-cyan">{stats.total_games}</p>
                  <p className="font-retro text-xs text-neon-cyan mt-1">PARTIDAS</p>
                </div>

                {/* Wins */}
                <div className="bg-neon-green/20 border-2 border-neon-green rounded-lg p-4 text-center">
                  <p className="font-retro text-3xl text-neon-green">{stats.wins}</p>
                  <p className="font-retro text-xs text-neon-green mt-1">VICTORIAS</p>
                </div>

                {/* Losses */}
                <div className="bg-neon-pink/20 border-2 border-neon-pink rounded-lg p-4 text-center">
                  <p className="font-retro text-3xl text-neon-pink">{stats.losses}</p>
                  <p className="font-retro text-xs text-neon-pink mt-1">DERROTAS</p>
                </div>

                {/* Ties */}
                <div className="bg-neon-yellow/20 border-2 border-neon-yellow rounded-lg p-4 text-center">
                  <p className="font-retro text-3xl text-neon-yellow">{stats.ties}</p>
                  <p className="font-retro text-xs text-neon-yellow mt-1">EMPATES</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neon-cyan/20 border-t-2 border-neon-cyan p-4 text-center">
          <button
            onClick={onClose}
            className="ui-button bg-neon-pink/30 hover:bg-neon-pink/50 border-2 border-neon-pink text-neon-pink hover:shadow-neon-pink"
          >
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
};
